import { useCallback, useEffect, useRef, useState } from "react";
import { useApi } from "../lib/useApi";
import { ApiError } from "../lib/api";
import { useSetTopbarTabs } from "../components/TopbarTabs";

export function SampleProjectMasterPage() {
  const api = useApi();
  useSetTopbarTabs([{ id: "master", label: "Sample project master", active: true }]);

  const [status, setStatus] = useState<Awaited<ReturnType<typeof api.getCurrentMasterProject>> | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(false);

  // The source project id is pre-filled from the looked-up live value but is editable,
  // so an operator can copy a different project. `liveValueRef` lets us re-sync the field
  // to a fresh live value on refresh *only* while it still mirrors the live value — once
  // it's been overridden, we leave the typed value alone.
  const [sourceProjectId, setSourceProjectId] = useState("");
  const liveValueRef = useRef<string | null>(null);

  const [workspaceId, setWorkspaceId] = useState("");
  const [releasing, setReleasing] = useState(false);
  const [releaseError, setReleaseError] = useState<string | null>(null);
  const [release, setRelease] = useState<Awaited<ReturnType<typeof api.releaseMasterProject>> | null>(null);

  const loadStatus = useCallback(async () => {
    setLoadingStatus(true);
    setStatusError(null);
    try {
      const s = await api.getCurrentMasterProject();
      const prevLive = liveValueRef.current;
      liveValueRef.current = s.liveValue;
      setSourceProjectId((prev) => {
        const trackingLive = prev.trim() === "" || prev.trim() === (prevLive ?? "");
        return trackingLive ? (s.liveValue ?? "") : prev;
      });
      setStatus(s);
    } catch (err) {
      setStatusError(err instanceof ApiError ? err.message : `${err}`);
    } finally {
      setLoadingStatus(false);
    }
  }, [api]);

  useEffect(() => { void loadStatus(); }, [loadStatus]);

  async function dispatchRelease() {
    const src = sourceProjectId.trim();
    if (!src) return;
    setReleasing(true);
    setReleaseError(null);
    setRelease(null);
    try {
      // Workspace is optional: when left blank, land the copy in the *source project's* own
      // workspace by resolving it at submit time — driven off whatever source id is entered
      // above (the live value or a typed override), not the live value specifically.
      let ws = workspaceId.trim();
      if (!ws) {
        ws = (await api.getProjectWorkspace(src)).workspaceId;
      }
      const res = await api.releaseMasterProject({
        sampleProjectId: src,
        workspaceId: ws,
      });
      setRelease(res);
    } catch (err) {
      setReleaseError(err instanceof ApiError ? err.message : `${err}`);
    } finally {
      setReleasing(false);
    }
  }

  const effectiveSource = sourceProjectId.trim();
  const isOverride = effectiveSource !== "" && effectiveSource !== (status?.liveValue ?? "");

  return (
    <>
      <div className="page-head reveal" data-d="1">
        <div className="crumb">Operation 02 · Sample project master</div>
        <h1>Sample project master</h1>
        <p>
          Take a verbatim copy of the current master sample project — or any project id you enter below — with no
          anonymization, into the source's own workspace. Once you've eyeballed the copy you can promote its id to be
          the active master separately — this tool only makes the copy, it doesn't change any live settings.
        </p>
      </div>

      <section className="panel reveal" data-d="2">
        <header>
          <span className="idx">01</span> Current master
          <span className="hint">live silo env</span>
        </header>
        <div className="body">
          {statusError && (
            <div className="callout error">
              <span className="mk">err</span>
              <span>{statusError}</span>
            </div>
          )}
          {status && (
            <>
              <dl className="spec">
                <dt>statefulset</dt>
                <dd className="mono">{status.statefulSet}</dd>
                <dt>env key</dt>
                <dd className="mono">{status.envName}</dd>
                <dt>live value</dt>
                <dd className="mono">{status.liveValue ?? <span className="faint">(unset)</span>}</dd>
              </dl>
              <div className="btn-row" style={{ marginTop: 14 }}>
                <button className={"btn btn-ghost" + (loadingStatus ? " loading" : "")} disabled={loadingStatus} onClick={() => void loadStatus()}>
                  <span className="glyph" /> Refresh
                </button>
              </div>
            </>
          )}
        </div>
      </section>

      {status && (
        <section className="panel reveal" data-d="3">
          <header>
            <span className="idx">02</span> Release new copy
            <span className="hint">defaults to source workspace</span>
          </header>
          <div className="body">
            <div className="field">
              <label>Source project id <span className="req">*</span></label>
              <input
                type="text"
                placeholder={status.liveValue ?? "project id to copy"}
                spellCheck={false}
                autoComplete="off"
                value={sourceProjectId}
                onChange={(e) => setSourceProjectId(e.target.value)}
              />
              <div className="hint">
                Pre-filled with the live master above; type a different project id to copy that one instead.
                {isOverride ? " Overriding the live value." : null}
              </div>
            </div>
            <div className="field">
              <label>Workspace id <span className="opt">— optional</span></label>
              <input
                type="text"
                placeholder="leave blank to use the source project's workspace"
                spellCheck={false}
                autoComplete="off"
                value={workspaceId}
                onChange={(e) => setWorkspaceId(e.target.value)}
              />
              <div className="hint">
                Leave blank and the copy lands in the source project's own workspace, resolved from the source id
                above; set it only to land the copy elsewhere. The copy is verbatim: members are copied as-is (no
                anonymization, owner unchanged).
              </div>
            </div>
            {releaseError && (
              <div className="callout error" style={{ marginTop: 14 }}>
                <span className="mk">err</span>
                <span>{releaseError}</span>
              </div>
            )}
            <div className="btn-row" style={{ marginTop: 14 }}>
              <button
                className={"btn btn-primary" + (releasing ? " loading" : "")}
                disabled={releasing || !effectiveSource}
                onClick={dispatchRelease}
              >
                <span className="glyph" /> Release verbatim copy
              </button>
              {isOverride && (
                <button
                  className="btn btn-ghost"
                  disabled={releasing}
                  onClick={() => setSourceProjectId(status.liveValue ?? "")}
                >
                  <span className="glyph" /> Use live value
                </button>
              )}
              <span className="faint mono" style={{ fontSize: 11 }}>
                source ⟶ {effectiveSource || "—"}
                {isOverride ? " (override)" : null}
              </span>
            </div>
          </div>
        </section>
      )}

      {release && (
        <section className="panel reveal" data-d="4" style={{ marginTop: 22 }}>
          <header>
            <span className="idx" style={{ color: "var(--lime)" }}>✓</span> Copy dispatched
          </header>
          <div className="body">
            <div className="callout ok" style={{ marginBottom: 16 }}>
              <span className="mk">ok</span>
              <span>
                The copy is running. Wait a few minutes, then verify the new project at the id below. Promoting it
                to be the active master is a separate, manual step.
              </span>
            </div>
            <dl className="spec">
              <dt>job</dt>
              <dd className="mono">{release.jobName}</dd>
              <dt>workspace</dt>
              <dd className="mono">{release.workspaceId ?? <span className="faint">(source workspace)</span>}</dd>
              <dt>source</dt>
              <dd className="mono">{release.sourceProjectId}</dd>
              <dt>new project id</dt>
              <dd className="mono"><strong>{release.newProjectId}</strong></dd>
            </dl>
          </div>
        </section>
      )}
    </>
  );
}
